import { useCallback, useEffect, useRef, useState } from 'react';
import { callFn } from './api';

export interface ApiState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: (silent?: boolean) => Promise<void>;
  setData: (v: T | null | ((p: T | null) => T | null)) => void;
}

/** Load a helm_api action on mount / when its arguments change; optionally poll and refresh when the tab regains focus. */
export function useApi<T>(action: string | null, payload: Record<string, unknown> = {}, opts: { poll?: number } = {}): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!!action);
  const seq = useRef(0);
  const key = JSON.stringify([action, payload]);
  const keyRef = useRef(key);
  keyRef.current = key;

  const reload = useCallback(async (silent = false) => {
    if (!action) return;
    const my = ++seq.current;
    if (!silent) setLoading(true);
    try {
      const d = await callFn<T>(action, JSON.parse(keyRef.current)[1] as Record<string, unknown>);
      if (my === seq.current) { setData(d); setError(null); }
    } catch (e) {
      if (my === seq.current) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    setData(null);
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!opts.poll) return;
    const id = window.setInterval(() => { if (!document.hidden) void reload(true); }, opts.poll);
    const vis = () => { if (!document.hidden) void reload(true); };
    document.addEventListener('visibilitychange', vis);
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', vis); };
  }, [opts.poll, reload]);

  return { data, error, loading, reload, setData };
}
